import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Calendar, User } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getArticleBySlug, getArticles } from '../lib/api';

const ArticleDetailPage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setArticle(null);
    setRelated([]);

    getArticleBySlug(slug)
      .then((res) => {
        setArticle(res.data);
        return getArticles({ limit: 4, category: res.data.category });
      })
      .then((res) => {
        const others = (res.data || []).filter((a) => a.slug !== slug).slice(0, 3);
        setRelated(others);
      })
      .catch(() => setError('Article not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  // SEO: update title / meta / canonical once article is loaded
  useEffect(() => {
    if (!article) return;
    document.title = article.metaTitle || article.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', article.metaDescription || article.subtitle || '');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `https://vietclinics.com/news/${article.slug}`;

    return () => {
      document.title = "VietClinics — Vietnam's Trusted Healthcare Marketplace";
    };
  }, [article]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-4 py-32 text-center">
          <div className="inline-block w-8 h-8 border-4 border-[#1FA7A1] border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-slate-500 text-sm">Loading article…</p>
        </div>
        <Footer />
      </div>
    );
  }

  // Error / not found state
  if (error || !article) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-4 py-32 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Article not found</h1>
          <p className="text-slate-500 mb-8">This article may have moved or no longer exists.</p>
          <Link to="/" className="inline-flex items-center gap-2 text-[#1FA7A1] font-semibold hover:underline">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section
        className="pt-14 pb-10"
        style={{ background: `linear-gradient(160deg, ${article.categoryColor}12 0%, #f8fafc 70%)` }}
      >
        <div className="container mx-auto px-4 max-w-4xl">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-slate-400 hover:text-[#1FA7A1] text-sm mb-8 transition-colors"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>

          <Link to={article.categoryTo}>
            <span
              className="inline-block text-xs font-bold text-white px-3 py-1.5 rounded-full mb-5 hover:opacity-90 transition-opacity"
              style={{ background: article.categoryColor }}
              data-testid="article-category-tag"
            >
              {article.category}
            </span>
          </Link>

          <h1
            className="font-extrabold text-slate-900 tracking-tight leading-tight mb-5"
            style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)' }}
            data-testid="article-title"
          >
            {article.title}
          </h1>

          <p
            className="text-slate-500 text-lg leading-relaxed mb-8 max-w-3xl"
            data-testid="article-subtitle"
          >
            {article.subtitle}
          </p>

          <div className="flex flex-wrap items-center gap-5 text-sm text-slate-400 border-t border-slate-200 pt-5">
            <span className="flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" />
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              {article.publishDate || article.date}
            </span>
          </div>
        </div>
      </section>

      {/* ── Featured Image ───────────────────────────────────────────────── */}
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="rounded-2xl overflow-hidden shadow-md" style={{ height: 420 }}>
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1200'; }}
            data-testid="article-featured-image"
          />
        </div>
      </div>

      {/* ── Article Content ──────────────────────────────────────────────── */}
      <article
        className="container mx-auto px-4 max-w-3xl py-14"
        data-testid="article-content"
      >
        {article.content.map((block, i) => {
          if (block.type === 'h2') {
            return (
              <h2
                key={i}
                className="text-xl md:text-2xl font-extrabold text-slate-900 mt-12 mb-4 leading-snug"
              >
                {block.text}
              </h2>
            );
          }
          if (block.type === 'paragraph') {
            return (
              <p key={i} className="text-slate-600 leading-relaxed text-base mb-5">
                {block.text}
              </p>
            );
          }
          if (block.type === 'bullets') {
            return (
              <ul key={i} className="mb-7 space-y-3 pl-1">
                {block.items.map((item, j) => (
                  <li key={j} className="flex items-start gap-3 text-slate-600 text-base leading-relaxed">
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2.5"
                      style={{ background: article.categoryColor }}
                    />
                    {item}
                  </li>
                ))}
              </ul>
            );
          }
          return null;
        })}
      </article>

      {/* ── CTA Section ──────────────────────────────────────────────────── */}
      <section
        className="py-16"
        style={{ background: 'linear-gradient(135deg, #0f2b55 0%, #1E5BA8 55%, #1FA7A1 100%)' }}
        data-testid="article-cta-section"
      >
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3 tracking-tight">
            Ready to Find the Right Clinic?
          </h2>
          <p className="text-white/70 text-sm mb-8 max-w-xl mx-auto leading-relaxed">
            Explore treatment options across Vietnam and submit a confidential enquiry — no booking fees.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/treatments">
              <button
                className="bg-white text-[#1FA7A1] font-bold rounded-full px-8 py-3.5 flex items-center gap-2 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
                style={{ boxShadow: '0 8px 20px rgba(0,0,0,0.15)' }}
                data-testid="cta-explore-treatments-btn"
              >
                Explore Treatments <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
            <Link to="/contact">
              <button
                className="text-white font-semibold rounded-full px-8 py-3.5 border border-white/30 hover:bg-white/15 transition-all duration-200 backdrop-blur-sm"
                data-testid="cta-free-advice-btn"
              >
                Get Free Advice
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Related Articles ─────────────────────────────────────────────── */}
      <section className="py-16" style={{ background: '#F8FAFC' }} data-testid="related-articles-section">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-xl font-extrabold text-slate-900 mb-8">Related Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((rel) => (
              <Link
                key={rel.slug}
                to={`/news/${rel.slug}`}
                className="group bg-white rounded-[18px] overflow-hidden border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col"
                style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}
                data-testid={`related-card-${rel.slug}`}
                aria-label={`Read article: ${rel.title}`}
              >
                <div className="overflow-hidden h-40">
                  <img
                    src={rel.image}
                    alt={rel.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800'; }}
                  />
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <span
                    className="text-[10px] font-bold text-white px-2.5 py-1 rounded-full w-max mb-3"
                    style={{ background: rel.categoryColor }}
                  >
                    {rel.category}
                  </span>
                  <h3 className="font-bold text-slate-900 text-sm leading-snug line-clamp-2 group-hover:text-[#1FA7A1] transition-colors mb-3 flex-1">
                    {rel.title}
                  </h3>
                  <span className="text-xs text-[#1FA7A1] font-semibold flex items-center gap-1">
                    Read article <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ArticleDetailPage;
